import { useTranslation } from 'react-i18next';
import { Button } from '@c2fo/liquidity';
import { CheckDoubleIcon, CircleMinusIcon, CirclePlusIcon, FilterListIcon } from '@c2fo/liquidity/icons';
import { PlayableInvoice } from '@/enterprise/data/usePlayableInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useRestrictions from '@/utils/useRestrictions';
import { EditInvoicesReferrer } from './EditInvoices';
import { useEditInvoicesContext } from './EditInvoicesContext';
import useInvoiceEdits from './useInvoiceEdits';
import useSelectedInvoices from './useSelectedInvoices';

interface EditInvoicesToolbarProps {
  currentCursor: string | null;
  currentTabAmount: number;
  currentTabCount: number;
  hasActiveOffer?: boolean;
  hasRecurringRule: boolean;
  isFloating: boolean | null;
  invoices?: PlayableInvoice[];
  referrer: EditInvoicesReferrer;
  setShowInvoiceCriteriaModal: (open: boolean) => void;
  subscribeToMarketStats: boolean;
  tableType: 'included' | 'excluded';
  takerMarket: TakerMarket;
}

const EditInvoicesToolbar = ({
  currentCursor,
  currentTabAmount,
  currentTabCount,
  hasActiveOffer = false,
  hasRecurringRule,
  isFloating = false,
  invoices,
  referrer,
  setShowInvoiceCriteriaModal,
  subscribeToMarketStats,
  tableType,
  takerMarket,
}: EditInvoicesToolbarProps) => {
  const { t } = useTranslation();
  const { trackEnterpriseEvent } = useReporting();
  const { asCurrency, asNumber } = useLocaleFormat();
  const { dispatch, state } = useEditInvoicesContext();
  const { getRestrictions } = useRestrictions();
  const { canEditInvoices } = getRestrictions([takerMarket]);

  const { handleIncludeAllInvoices, handleExcludeAllInvoices, handleInclude, handleExclude } = useInvoiceEdits({
    onLoadingChange: (loading) => dispatch({ type: 'SET_LOADING', payload: { loading } }),
    onProgressChange: (current, total) => dispatch({ type: 'SET_PROGRESS', payload: { current, total } }),
    onResetSelection: () => dispatch({ type: 'RESET_SELECTED' }),
    getSelectedInvoices: () => state.selected,
    onError: (errors) => dispatch({ type: 'SET_ERRORS', payload: { errors } }),
    subscribeToMarketStats,
  });

  const {
    state: selectedInvoicesState,
    isAllCurrentPageSelected,
    isAllInvoicesSelected,
    handleSelectAllInvoices,
  } = useSelectedInvoices({
    currentCursor,
    invoices,
    tableType,
  });

  const trackBulkAction = (action: string, selectionMode: string) => {
    trackEnterpriseEvent('invoices::bulk-action', {
      makerOrgUuid: takerMarket.makerOrganizationUuid,
      marketUuid: takerMarket.marketUuid,
      takerUuid: takerMarket.takerDivisionUuid,
      action,
      selectionMode,
      currentTab: tableType,
      referrer,
    });
  };

  const disabled = hasRecurringRule || hasActiveOffer || !canEditInvoices;
  // no-selection: secondary (inverse when floating) — secondary-500 icon on light bg, white icon on dark bg
  const defaultIconColor = isFloating ? 'fill-white' : 'fill-secondary-500';
  // selected: primary (inverse when floating) — white icon on dark bg, secondary-500 icon on white bg
  const selectedIconColor = isFloating ? 'fill-secondary-500' : 'fill-white';

  const getContent = () => {
    switch (disabled ? 'none' : selectedInvoicesState.selectionMode) {
      case 'none': {
        const configs = [
          {
            icon: <FilterListIcon className={defaultIconColor} />,
            intercomDataTarget: 'invoiceEditCriteriaButton',
            onClick: () => {
              trackEnterpriseEvent('invoices::criteria-modal-opened', {
                makerOrgUuid: takerMarket.makerOrganizationUuid,
                marketUuid: takerMarket.marketUuid,
                takerUuid: takerMarket.takerDivisionUuid,
                action: 'include',
                referrer,
              });

              setShowInvoiceCriteriaModal(true);
            },
            text: t('invoices.includeByCriteria'),
          },
          {
            icon: <CirclePlusIcon className={defaultIconColor} />,
            onClick: () => {
              trackBulkAction('include-all', 'none');
              handleIncludeAllInvoices([takerMarket]);
            },
            text: t('invoices.includeAllInvoices'),
          },
          {
            icon: <CircleMinusIcon className={defaultIconColor} />,
            onClick: () => {
              trackBulkAction('exclude-all', 'none');
              handleExcludeAllInvoices([takerMarket]);
            },
            text: t('invoices.excludeAllInvoices'),
          },
        ];

        return (
          <>
            {configs.map((config) => (
              <Button
                key={config.text}
                className="[&>span:first-child]:size-5"
                disabled={disabled}
                icon={config.icon}
                inverse={isFloating}
                onClick={config.onClick}
                size="md"
                variant="secondary"
                {...(config.intercomDataTarget && { 'data-intercom-target': config.intercomDataTarget })}
              >
                {config.text}
              </Button>
            ))}
          </>
        );
      }

      case 'all': {
        const config =
          tableType === 'excluded'
            ? {
                icon: <CirclePlusIcon className={selectedIconColor} />,
                onClick: () => {
                  trackBulkAction('include-selected', 'all');
                  handleIncludeAllInvoices([takerMarket]);
                },
                text: t('invoices.includeSelected', {
                  formattedCount: asNumber({ value: currentTabCount }),
                  count: currentTabCount,
                  formattedAmount: asCurrency(currentTabAmount, takerMarket.currency),
                }),
              }
            : {
                icon: <CircleMinusIcon className={selectedIconColor} />,
                onClick: () => {
                  trackBulkAction('exclude-selected', 'all');
                  handleExcludeAllInvoices([takerMarket]);
                },
                text: t('invoices.excludeSelected', {
                  formattedCount: asNumber({ value: currentTabCount }),
                  count: currentTabCount,
                  formattedAmount: asCurrency(currentTabAmount, takerMarket.currency),
                }),
              };

        return (
          <Button
            className="[&_span:first-child]:size-5"
            icon={config.icon}
            inverse={isFloating}
            onClick={config.onClick}
            size="md"
            variant="primary"
          >
            {config.text}
          </Button>
        );
      }

      default: {
        const config =
          tableType === 'excluded'
            ? {
                icon: <CirclePlusIcon className={selectedIconColor} />,
                onClick: () => {
                  trackBulkAction('include-selected', 'partial');
                  handleInclude([takerMarket]);
                },
                text: t('invoices.includeSelected', {
                  formattedCount: asNumber({ value: selectedInvoicesState.selected.length }),
                  count: selectedInvoicesState.selected.length,
                  formattedAmount: asCurrency(
                    selectedInvoicesState.selected.reduce((acc, invoice) => acc + invoice.amount, 0),
                    takerMarket.currency
                  ),
                }),
              }
            : {
                icon: <CircleMinusIcon className={selectedIconColor} />,
                onClick: () => {
                  trackBulkAction('exclude-selected', 'partial');
                  handleExclude([takerMarket]);
                },
                text: t('invoices.excludeSelected', {
                  formattedCount: asNumber({ value: selectedInvoicesState.selected.length }),
                  count: selectedInvoicesState.selected.length,
                  formattedAmount: asCurrency(
                    selectedInvoicesState.selected.reduce((acc, invoice) => acc + invoice.amount, 0),
                    takerMarket.currency
                  ),
                }),
              };
        return (
          <>
            <Button
              className="[&_span:first-child]:size-5"
              icon={config.icon}
              inverse={isFloating}
              onClick={config.onClick}
              size="md"
              variant="primary"
            >
              {config.text}
            </Button>
            {isAllCurrentPageSelected && !isAllInvoicesSelected && currentTabCount > 25 && (
              <Button
                className="[&_span:first-child]:size-5"
                icon={<CheckDoubleIcon className={selectedIconColor} />}
                inverse={isFloating}
                onClick={() => {
                  trackEnterpriseEvent('invoices::select-all-expanded', {
                    makerOrgUuid: takerMarket.makerOrganizationUuid,
                    marketUuid: takerMarket.marketUuid,
                    takerUuid: takerMarket.takerDivisionUuid,
                    currentTab: tableType,
                    totalCount: currentTabCount,
                    referrer,
                  });
                  handleSelectAllInvoices();
                }}
                size="md"
                variant="primary"
              >
                {t('invoices.selectAll', {
                  formattedCount: asNumber({ value: currentTabCount }),
                  count: currentTabCount,
                })}
              </Button>
            )}
          </>
        );
      }
    }
  };

  return <div className="flex shrink-0 gap-2">{getContent()}</div>;
};

export default EditInvoicesToolbar;
